import React, { useCallback } from "react";
import { graphql } from "react-relay";
import { useHistory } from "react-router";

import { faList, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUsers } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import AppContext from "~/common/AppContext";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { PrimaryNavLink } from "~/nav/panels/shared";
import CreateObjective from "~/okrs/CreateObjective";
import { eventEmitter as okrEventEmitter } from "~/okrs/events";
import CreateTeamModal from "~/team/CreateOrEditTeam";
import { useModals } from "~/ui";
import Avatar from "~/ui/avatar";
import { IconButton } from "~/ui/legacyFellowIndex";

import TeamsSection from "../Home/Sections/TeamsGroup";
import PanelSection from "../shared/PanelSection";

import type { ObjectivesPanelQuery as QueryType } from "~/__generated__/ObjectivesPanelQuery.graphql";

import panelStyles from "~/nav/panels/shared/NavPanel.module.css";
import panelSectionStyles from "~/nav/panels/shared/PanelSection.module.css";

export default function ObjectivesPanel() {
	const { user } = React.useContext(AppContext);
	const modals = useModals();
	const history = useHistory();

	const { viewer } = useLazyLoadQuery<QueryType>(
		graphql`
			query ObjectivesPanelQuery {
				viewer {
					myTeams {
						id
						archived
						...TeamLink_info
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const onTeamCreated = useCallback(
		(teamId: string) => {
			history.push(`/teams/${teamId}/`);
		},
		[history],
	);

	const refetch = useCallback(() => {
		okrEventEmitter.emit("Okrs.refetch");
	}, []);

	const showCreateTeamModal = useCallback(() => {
		modals.open(({ close }) => <CreateTeamModal onClose={close} onCreated={onTeamCreated} />);
	}, [modals, onTeamCreated]);

	const onClickCreateObjective = useCallback(() => {
		modals.open(({ close }) => <CreateObjective onClose={close} refetch={refetch} />);
	}, [modals, refetch]);

	return (
		<div className={panelStyles.panel}>
			<div className={panelStyles.panelHeader}>
				<h1 className={panelStyles.panelTitle}>Objectives</h1>
				<IconButton icon={faPlus} title="Create objective" onClick={onClickCreateObjective} />
			</div>
			<div className={panelStyles.main}>
				<div className={cx(panelSectionStyles.panelSection, panelSectionStyles.panelSectionPrimary)}>
					<div className={panelSectionStyles.panelSectionContent}>
						<PrimaryNavLink to="/objectives/all/">
							<FontAwesomeIcon icon={faList} className={panelSectionStyles.mediumIcon} />
							All Objectives
						</PrimaryNavLink>
						<PrimaryNavLink exact to="/objectives/">
							{user ? <Avatar user={user} className={panelSectionStyles.streamIcon} /> : null}
							My Objectives
						</PrimaryNavLink>
					</div>
				</div>
				<div>
					<PanelSection
						title={"My Teams"}
						icon={faUsers}
						plusButtonTooltip={"Create team"}
						onPlusClick={showCreateTeamModal}
					>
						<TeamsSection items={viewer.myTeams ?? []} />
					</PanelSection>
				</div>
			</div>
		</div>
	);
}
